import { useState, useCallback, useRef, useEffect } from 'react';
import type { BulkActionType, Planner, PlannerEventRow } from '@/types/planner.types';
import { getActiveAccount } from '@/services';
import { useBatchEventActionMutation } from '@/store/api/eventService';
import type {
  BatchEventActionArg,
  BatchEventResultItem,
} from '@/store/api/eventService/eventServiceApi';
import { EVENT_STATE_TO_STATUS } from '@/utils/plannerEventMapper';
import type { EventState } from '@/store/models/events/event-state';

const BATCH_SIZE = 10;

export interface UseEventActionsOptions {
  selectedEventIds: Set<string>;
  events: PlannerEventRow[];
  onSelectionClear: () => void;
  onEventsUpdate: (events: PlannerEventRow[]) => void;
}

export interface ActionResult {
  type: BulkActionType;
  successCount: number;
  failedCount: number;
  plannerName: string;
}

export interface UseEventActionsReturn {
  activeDialog: BulkActionType | null;
  isProcessing: boolean;
  error: string | null;
  selectedPlanner: Planner | null;
  openClaimDialog: () => void;
  openHandoffDialog: () => void;
  closeDialog: () => void;
  confirmAction: () => void;
  setSelectedPlanner: (planner: Planner | null) => void;
  snapshotCount: number;
  lastActionResult: ActionResult | null;
  lastActionError: string | null;
  clearLastAction: () => void;
}

function chunk<T>(items: readonly T[], size: number): T[][] {
  if (size <= 0) return [items.slice()];
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

export const useEventActions = ({
  selectedEventIds,
  events,
  onSelectionClear: _onSelectionClear,
  onEventsUpdate,
}: UseEventActionsOptions): UseEventActionsReturn => {
  const [activeDialog, setActiveDialog] = useState<BulkActionType | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlanner, setSelectedPlanner] = useState<Planner | null>(null);
  const snapshotIdsRef = useRef<Set<string>>(new Set());
  const [snapshotCount, setSnapshotCount] = useState(0);
  const isMountedRef = useRef(true);
  const [lastActionError, setLastActionError] = useState<string | null>(null);
  const [lastActionResult, setLastActionResult] = useState<ActionResult | null>(null);

  const [batchEventAction] = useBatchEventActionMutation();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const openDialog = useCallback(
    (type: BulkActionType) => {
      snapshotIdsRef.current = new Set(selectedEventIds);
      setSnapshotCount(selectedEventIds.size);
      setActiveDialog(type);
      setError(null);
      setSelectedPlanner(null);
    },
    [selectedEventIds]
  );

  const openClaimDialog = useCallback(() => openDialog('claim'), [openDialog]);
  const openHandoffDialog = useCallback(() => openDialog('handoff'), [openDialog]);

  const closeDialog = useCallback(() => {
    setActiveDialog(null);
    setError(null);
    setSelectedPlanner(null);
    setIsProcessing(false);
  }, []);

  const confirmAction = useCallback(() => {
    if (!activeDialog) return;

    let plannerId = '';
    let plannerName = 'Current User';

    if (activeDialog === 'claim') {
      const account = getActiveAccount();
      plannerId = account?.username ?? '';
      plannerName = account?.name ?? 'Current User';
    } else if (activeDialog === 'handoff') {
      if (!selectedPlanner) {
        setError('No planner selected');
        return;
      }
      plannerId = selectedPlanner.email;
      plannerName = selectedPlanner.displayName;
    }

    const action: BatchEventActionArg['action'] = activeDialog === 'claim' ? 'CLAIM' : 'HANDOFF';
    const currentActionType = activeDialog;

    const ids = snapshotIdsRef.current;
    const batchEvents = events
      .filter((e) => ids.has(e.eventId))
      .map((e) => ({ eventId: e.eventId, buildingId: e.buildingId }));

    if (batchEvents.length === 0) {
      return;
    }

    setIsProcessing(true);
    setError(null);
    setLastActionError(null);
    setLastActionResult(null);

    const chunks = chunk(batchEvents, BATCH_SIZE);

    void (async () => {
      try {
        // Fire all chunks in parallel; collect per-chunk outcomes without letting one
        // failed chunk reject the whole Promise.all.
        const settled = await Promise.all(
          chunks.map((chunkEvents) =>
            batchEventAction({
              action,
              events: chunkEvents,
              assignment: { plannerId, plannerName },
            })
              .unwrap()
              .then(
                (resp) => ({
                  kind: 'ok' as const,
                  results:
                    (resp as { data?: { results?: BatchEventResultItem[] } }).data?.results ?? [],
                }),
                (err: unknown) => ({
                  kind: 'err' as const,
                  message: extractErrorMessage(err),
                  chunkEventIds: chunkEvents.map((e) => e.eventId),
                })
              )
          )
        );

        if (!isMountedRef.current) return;

        // Aggregate across all chunks.
        const successMap = new Map<string, string | undefined>();
        const failedResults: BatchEventResultItem[] = [];
        const chunkErrorMessages: string[] = [];

        for (const outcome of settled) {
          if (outcome.kind === 'ok') {
            for (const r of outcome.results) {
              if (r.status === 'SUCCESS') {
                successMap.set(r.eventId, r.newState);
              } else {
                failedResults.push(r);
              }
            }
          } else {
            // Whole chunk failed at transport / HTTP level — count every event in it as failed.
            for (const eventId of outcome.chunkEventIds) {
              failedResults.push({
                eventId,
                status: 'FAILED',
                errorMessage: outcome.message,
              });
            }
            chunkErrorMessages.push(outcome.message);
          }
        }

        const failedCount = failedResults.length;

        if (successMap.size > 0) {
          const updatedEvents = events.map((event) => {
            const newState = successMap.get(event.eventId);
            if (newState !== undefined) {
              const mappedStatus = EVENT_STATE_TO_STATUS[newState as EventState];
              return {
                ...event,
                owner: { name: plannerName },
                ...(mappedStatus ? { status: mappedStatus } : {}),
              };
            }
            if (successMap.has(event.eventId)) {
              return { ...event, owner: { name: plannerName } };
            }
            return event;
          });
          onEventsUpdate(updatedEvents);
        }

        setLastActionResult({
          type: currentActionType,
          successCount: successMap.size,
          failedCount,
          plannerName,
        });

        if (failedCount > 0) {
          // Collect distinct error messages from per-item failures and per-chunk transport
          // errors, then join them so the planner sees the full set of reasons.
          const itemMessages = failedResults.map((r) => r.errorMessage).filter(Boolean) as string[];
          const allMessages = [...itemMessages, ...chunkErrorMessages];
          const uniqueMessages = [...new Set(allMessages)];
          const detail = uniqueMessages.length > 0 ? uniqueMessages.join('; ') : 'Unknown error';

          if (successMap.size > 0) {
            setLastActionError(`${successMap.size} succeeded, ${failedCount} failed: ${detail}`);
          } else {
            // Pure transport failure on every chunk: use the generic copy the screen toast expects
            // so we don't surface a chain of duplicated "Network error" lines.
            if (
              successMap.size === 0 &&
              failedResults.every((r) => r.errorCode === undefined && r.errorMessage)
            ) {
              const onlyTransport = chunkErrorMessages.length === chunks.length;
              setLastActionError(
                onlyTransport ? 'An error occurred. Please try again.' : `Failed: ${detail}`
              );
            } else {
              setLastActionError(`Failed: ${detail}`);
            }
          }
        }

        setIsProcessing(false);
      } catch {
        setIsProcessing(false);
      }
    })();
  }, [activeDialog, events, selectedPlanner, batchEventAction, onEventsUpdate]);

  const clearLastAction = useCallback(() => {
    setLastActionError(null);
    setLastActionResult(null);
  }, []);

  return {
    activeDialog,
    isProcessing,
    error,
    selectedPlanner,
    openClaimDialog,
    openHandoffDialog,
    closeDialog,
    confirmAction,
    setSelectedPlanner,
    snapshotCount,
    lastActionResult,
    lastActionError,
    clearLastAction,
  };
};

function extractErrorMessage(err: unknown): string {
  if (err && typeof err === 'object') {
    const e = err as { data?: { message?: string }; error?: string; status?: string | number };
    if (typeof e.data?.message === 'string' && e.data.message.length > 0) return e.data.message;
    if (typeof e.error === 'string' && e.error.length > 0) return e.error;
    if (typeof e.status === 'string' && e.status.length > 0) return `Request failed (${e.status})`;
    if (typeof e.status === 'number') return `Request failed (${e.status})`;
  }
  return 'An error occurred. Please try again.';
}
